import { MsalModuleVersion } from './static';
import { resolveVersion } from './versioning/resolve-version';
import { createProxyProvider as createProxyProvider_v2 } from './v2/create-proxy-provider';
import { createProxyProvider as createProxyProvider_v4 } from './v4/create-proxy-provider';
/**
 * Creates a proxy provider for version compatibility.
 *
 * This function handles the creation of proxy providers that maintain
 * backward compatibility with different MSAL versions while using the
 * latest MSAL v4 implementation under the hood.
 *
 * @param provider - The base MSAL provider instance
 * @param version - The target version string (e.g., '2.0.0', '4.0.0')
 * @returns A proxy provider compatible with the specified version
 *
 * @template T - The provider interface type expected by the target version.
 * @throws {Error} If the resolved version is not supported.
 *
 * @example
 * ```typescript
 * const baseProvider = new MsalProvider(config);
 * const v2Proxy = createProxyProvider(baseProvider, '2.0.0');
 * ```
 */
export function createProxyProvider(provider, version) {
    // Resolve the requested version to determine which proxy to create
    const { enumVersion } = resolveVersion(version);
    // Build the version-appropriate proxy based on the resolved MSAL module version
    switch (enumVersion) {
        case MsalModuleVersion.V2:
            // Create v2-compatible proxy with legacy API adapters
            return createProxyProvider_v2(provider);
        case MsalModuleVersion.V4:
            // Create v4-compatible proxy (passthrough with v4 version metadata)
            return createProxyProvider_v4(provider);
        case MsalModuleVersion.V5:
            // Create transparent proxy for v5 - passes through to original provider
            return new Proxy(provider, {
                get: (target, prop) => {
                    // Delegate every known property/method to the underlying provider
                    switch (prop) {
                        case 'version': {
                            return target.version;
                        }
                        case 'msalVersion': {
                            return enumVersion;
                        }
                        case 'client': {
                            return target.client;
                        }
                        case 'account': {
                            return target.account;
                        }
                        case 'acquireAccessToken': {
                            return target.acquireAccessToken.bind(target);
                        }
                        case 'acquireToken': {
                            return target.acquireToken.bind(target);
                        }
                        case 'login': {
                            return target.login.bind(target);
                        }
                        case 'logout': {
                            return target.logout.bind(target);
                        }
                        case 'handleRedirect': {
                            return target.handleRedirect.bind(target);
                        }
                        case 'createProxyProvider': {
                            return target.createProxyProvider.bind(target);
                        }
                        case 'initialize': {
                            return () => {
                                // noop - initialize is handled by the provider, not the proxy
                            };
                        }
                        default: {
                            // backstop to prevent then from being called - this is not an async operation
                            if (prop === 'then')
                                return undefined;
                            // Exhaustive check: TypeScript-only guard to ensure all IMsalProvider keys are handled
                            const exhausted = prop;
                            return target[exhausted];
                        }
                    }
                },
            });
        default:
            throw new Error(`Version ${version} is not supported`);
    }
}
//# sourceMappingURL=create-proxy-provider.js.map